import cx from 'classnames';
import get from 'lodash/get';
import startsWith from 'lodash/startsWith';
import React, { PureComponent, useEffect, useMemo, useState } from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import de from 'react-phone-number-input/locale/de.json';
import es from 'react-phone-number-input/locale/es.json';
import fr from 'react-phone-number-input/locale/fr.json';
import it from 'react-phone-number-input/locale/it.json';
import pt from 'react-phone-number-input/locale/pt.json';
import FieldContainer from '../components/FieldContainer';
import Icon from '../components/Icon';
import './PhoneNumberField.scss';
import { useRequest } from 'ahooks';
import validationService, { useConfirmationCode } from '../services/validation.service';
import InputBlocker from '../components/InputBlocker';
import { StringField } from './index';
import { isFieldLocked } from '../components/utils';
import i18n from '../translations';
import { alertPreviewSimulation, isPreviewSimulation } from '../utils/previewSimulation';

// The country names of the list in the language of the form. English is the library's own
const countryLabels = { de, es, fr, it, pt };

function hasOneOfPrefix(phoneNumber, prefixes) {
  if (!prefixes || prefixes.length === 0) return false;
  const withoutPlus = phoneNumber.replace('+', '').replace(/^00/, '');
  return !!prefixes.find((prefix) => startsWith(withoutPlus, prefix));
}

const CodeCheckIcon = ({ state }) => {
  switch (state) {
    case 'success':
      return <Icon className={`icon--${state}`} name="check" />;
    case 'error':
      return <Icon className={`icon--${state}`} name="times" />;
    case 'loading':
      return <Icon className={`icon--${state}`} name="refresh" spin />;
    default:
      return null;
  }
};

const SendButton = ({ phoneNumber, strings, disabled }) => {
  const { data, loading, run } = useRequest(() => validationService.sendCode(phoneNumber), { manual: true });
  const btn = get(strings, ['confirm', 'btn'], i18n.t('phone.confirm.send-code'));

  function handleSend(e) {
    e.preventDefault();
    e.stopPropagation();

    // A simulation of the back-office preview sends no SMS
    if (isPreviewSimulation()) {
      alertPreviewSimulation();
      return;
    }
    run();
  }

  return (
    <button type="button" className="af-form-button" onClick={handleSend} disabled={disabled || loading}>
      <span style={{ opacity: loading ? 0 : undefined }}>{btn}</span>
      {loading && (
        <div className="af-form-button__icon__container">
          <Icon name="refresh" spin />
        </div>
      )}
    </button>
  );
};

const ConfirmBySMS = ({ name, phoneNumber, strings, config }) => {
  const { codeLength = 6, prefixExceptions } = config;
  const [value, setValue] = useState('');
  const { loading, confirmationCode, error, checkCode } = useConfirmationCode();
  const confirmationRequired = get(strings, ['confirm', 'description'], i18n.t('phone.confirm.description'));
  const placeholder = get(strings, ['confirm', 'placeholder'], i18n.t('phone.confirm.placeholder'));

  useEffect(() => {
    // A simulation of the back-office preview sent no code: none to check
    if (value.length === codeLength && !isPreviewSimulation()) {
      // Time to try query...
      checkCode(phoneNumber, value);
    }
  }, [value]);

  const isSuccess = confirmationCode;
  const confirmationState = useMemo(() => {
    if (isSuccess) return 'success';
    if (value.length !== codeLength) return 'error';
    if (loading) return 'loading';
    return 'error';
  }, [isSuccess, value, loading]);

  if (hasOneOfPrefix(phoneNumber, prefixExceptions)) {
    return null; // No need to check
  }

  return (
    <div className="PhoneInput__confirmation">
      <p>{confirmationRequired}</p>
      <div>
        <SendButton phoneNumber={phoneNumber} strings={strings} disabled={isSuccess} />
        {/* The digit pad, and iOS offers the code received by SMS above the keyboard */}
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          className="af-string-field"
          value={value}
          disabled={isSuccess}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
        />
        {/* A simulation of the back-office preview sends no SMS: no code can come, none is asked */}
        <input
          name={`${name}Confirmation`}
          value={confirmationCode}
          required={!isPreviewSimulation()}
          style={{ width: 1, height: 1, opacity: 0, border: 0, padding: 0 }}
        />
        {/* No code is checked in a simulation: the code typed is neither right nor wrong */}
        {!isPreviewSimulation() && <CodeCheckIcon state={confirmationState} />}
      </div>
      {!isSuccess && error && (
        <p className="af-field-error" style={{ color: '#d30f00' }}>
          {error}
        </p>
      )}
    </div>
  );
};

export default class PhoneNumberField extends PureComponent {
  static defaultProps = {
    value: '',
    required: false,
  };

  render() {
    const {
      id,
      placeholder,
      label,
      mode,
      name,
      value,
      onChange,
      required,
      disabled,
      confirmBySMS = {},
      defaultCountry = 'FR',
      defaultValue,
      hideCountrySelector,
      strings = {},
      summary,
      locked,
    } = this.props;
    const { invalidPhoneNumber = i18n.t('errors.INVALID_PHONE_NUMBER') } = strings;
    if (disabled && !value) return null; // Hide disabled field
    const isLocked = isFieldLocked(name, locked);
    // Unbox phone array
    const stringValue = (Array.isArray(value) ? value.join('').replace('FR0', '+33') : value) || defaultValue;
    const isValid = !stringValue || stringValue.length === 0 || isValidPhoneNumber(stringValue);
    const error = (!isValid && stringValue && stringValue !== '+') || undefined;

    if (mode === 'summary') {
      if (summary?.hidden) {
        return null;
      }
      return <StringField label={summary?.label || label} mode="summary" value={stringValue} />;
    }

    return (
      <FieldContainer {...this.props}>
        <PhoneInput
          id={id}
          defaultCountry={defaultCountry}
          displayInitialValueAsLocalNumber={false}
          className={cx('af-phoneNumber-field', { invalid: error, hideCountrySelector })}
          name={name + 'Input'}
          required={required}
          disabled={disabled || isLocked}
          placeholder={placeholder}
          value={stringValue}
          onChange={(phone) => onChange(name, phone)}
          error={error}
          labels={countryLabels[i18n.language]}
        />
        {/* Duplicate with plain input to store the full full number instead of the local one */}
        {isValid && (
          <>
            <input name={name} value={stringValue} readOnly style={{ display: 'none' }} />
            {value && confirmBySMS && confirmBySMS.enabled && (
              <ConfirmBySMS name={name} phoneNumber={stringValue} strings={strings} config={confirmBySMS} />
            )}
          </>
        )}
        {value && value !== '+' && !isValid && (
          <div className="af-field-error" style={{ color: '#d30f00' }}>
            {invalidPhoneNumber}
          </div>
        )}
        {(!isValid || (required && !value)) && <InputBlocker name={name} />}
      </FieldContainer>
    );
  }
}
